import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { CandidatePagination } from "@/types/candidates"

/**
 * OKS-17 — Pagination footer.
 *
 * UX Spec §Pagination: "Showing X–Y of Z candidates · Prev · Page X
 * of Y · Next" (mobile collapses page numbers to "Page X of Y";
 * desktop ideally has numbered pages — V1 ships prev/next + page-of
 * indicator on all viewports for parity with the OKS-15 admin
 * pagination pattern).
 *
 * Disabled when offline (Inertia partial reload can't complete).
 *
 * Locked testid contract:
 *   - `candidate-list-pagination` — footer wrapper
 *   - `candidate-list-pagination-prev` — prev button
 *   - `candidate-list-pagination-next` — next button
 */
interface CandidatePaginationFooterProps {
  pagination: CandidatePagination
  perPage?: number
  onPageChange: (page: number) => void
  className?: string
}

export function CandidatePaginationFooter({
  pagination,
  perPage = 25,
  onPageChange,
  className,
}: CandidatePaginationFooterProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const { page, pages, count } = pagination

  if (count === 0) {
    return (
      <div
        data-testid="candidate-list-pagination"
        className={cn(
          "text-muted-foreground flex justify-center py-4 text-sm",
          className,
        )}
      >
        {t("candidates.list.pagination.showing_zero")}
      </div>
    )
  }

  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, count)
  const isFirst = page <= 1
  const isLast = page >= pages

  const disabledPrev = isFirst || !online
  const disabledNext = isLast || !online

  return (
    <nav
      data-testid="candidate-list-pagination"
      aria-label={t("candidates.list.pagination.page_of", { page, pages })}
      className={cn(
        "flex flex-col items-center gap-3 py-4 sm:flex-row sm:justify-between",
        className,
      )}
    >
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("candidates.list.pagination.showing", { from, to, count })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabledPrev}
          aria-disabled={disabledPrev}
          data-testid="candidate-list-pagination-prev"
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t("candidates.list.pagination.prev")}
        </Button>
        <span className="text-muted-foreground text-sm tabular-nums">
          {t("candidates.list.pagination.page_of", { page, pages })}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabledNext}
          aria-disabled={disabledNext}
          data-testid="candidate-list-pagination-next"
          onClick={() => onPageChange(page + 1)}
        >
          {t("candidates.list.pagination.next")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </nav>
  )
}
